import React, { Component, Fragment } from 'react';
import { withContext } from '../utils/StateProvider';
import { PerspectiveCamera } from '@react-three/drei'
import { useRef, useMemo, useEffect } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { coordsToPosition, combatFoeLayout } from '../utils/viewMath'
import { wallBeside } from '../utils/dungeon'
import { painted, wallMaps, wallVariant, wallsOf, floorMaps, ceilingMaps, CARPET } from '../utils/textures'
import { Effect, SpellLight } from './effects'

const deg2rad = degrees => degrees * (Math.PI / 180);

// How far from the party the dungeon is drawn, in squares
const DRAW_RADIUS = 12;

function Chest(props) {
  const { position } = props;
  
  const chest = painted('chest_wood')
  const chestTexture = chest.map
  const chestNormalMap = chest.normalMap
  
  return (
    <group position={position}>
      {/* Chest base */}
      <mesh position={[0, -0.35, 0]}>
        <boxGeometry args={[0.6, 0.3, 0.4]} />
        <meshStandardMaterial 
          map={chestTexture}
          normalMap={chestNormalMap}
          roughness={0.8}
          metalness={0.1}
        />
      </mesh>
      
      {/* Chest lid */}
      <mesh position={[0, -0.15, 0]}>
        <boxGeometry args={[0.6, 0.15, 0.4]} />
        <meshStandardMaterial 
          map={chestTexture}
          normalMap={chestNormalMap}
          roughness={0.8}
          metalness={0.1}
        />
      </mesh>
      
      {/* Gold trim on lid edges */}
      <mesh position={[0, -0.075, 0.21]}>
        <boxGeometry args={[0.62, 0.02, 0.02]} />
        <meshStandardMaterial 
          color="#4a3a1c"
          roughness={0.65}
          metalness={0.5}
        />
      </mesh>
      <mesh position={[0, -0.075, -0.21]}>
        <boxGeometry args={[0.62, 0.02, 0.02]} />
        <meshStandardMaterial 
          color="#4a3a1c"
          roughness={0.65}
          metalness={0.5}
        />
      </mesh>
      
      {/* Lock plate */}
      <mesh position={[0, -0.25, 0.21]}>
        <boxGeometry args={[0.06, 0.06, 0.02]} />
        <meshStandardMaterial 
          color="#4a3a1c"
          roughness={0.55}
          metalness={0.6}
        />
      </mesh>
      <mesh position={[0, -0.26, 0.225]}>
        <boxGeometry args={[0.02, 0.03, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.8} />
      </mesh>
    </group>
  )
}

// Stairways are cut into the wall beside their square, in the manner of
// Might and Magic III: a painted archway with steps running away into the
// dark. `wall` is the direction of the wall from the square.
const WALL_PANELS = {
  7: './assets/ui/stairs_up_wall.jpg',
  10: './assets/ui/stairs_down_wall.jpg',
}

// A trade's front is painted on the building beside its door, in the same way
// as a stairway. What the party actually uses is the counter inside.
const SHOP_FRONTS = {
  temple: './assets/ui/front_temple.jpg',
  magic: './assets/ui/front_magic.jpg',
  market: './assets/ui/front_market.jpg',
  training: './assets/ui/front_training.jpg',
  tavern: './assets/ui/front_tavern.jpg',
}

// One texture per panel, shared by every square that shows it. A painted town
// names its own way down — a sewer mouth, a tower, a castle gate — so panels
// are cached by file rather than by square.
const panelCache = {}
function panelTexture(url) {
  if (!panelCache[url]) {
    panelCache[url] = new THREE.TextureLoader().load(url)
    panelCache[url].colorSpace = THREE.SRGBColorSpace
  }
  return panelCache[url]
}

// Which way to turn the panel so it faces into the room
const PANEL_TURN = { '0,-1': 0, '1,0': -Math.PI / 2, '0,1': Math.PI, '-1,0': Math.PI / 2 }

function WallFront(props) {
  const { position, tile, wall, panel } = props
  const url = panel || WALL_PANELS[tile]
  const texture = useMemo(() => panelTexture(url), [url])
  const [dx, dz] = wall
  const turn = PANEL_TURN[`${dx},${dz}`] ?? 0

  return (
    <group position={position}>
      {/* The archway itself, set into the wall face */}
      <mesh position={[dx * 0.499, 0, dz * 0.499]} rotation={[0, turn, 0]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>
      {/* A worn sill at the foot of the archway */}
      <mesh position={[dx * 0.44, -0.475, dz * 0.44]} rotation={[0, turn, 0]}>
        <boxGeometry args={[0.72, 0.05, 0.12]} />
        <meshStandardMaterial color="#3f3a33" roughness={0.95} />
      </mesh>
    </group>
  )
}

// A slice of a front's picture, for the pieces of wall around a doorway. Each
// slice loads its own copy of the texture: a shared one cannot be offset two
// ways at once.
const sliceCache = {}
function panelSlice(url, [u, v, du, dv]) {
  const key = `${url}:${u},${v},${du},${dv}`
  if (!sliceCache[key]) {
    const texture = new THREE.TextureLoader().load(url)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.repeat.set(du, dv)
    texture.offset.set(u, v)
    sliceCache[key] = texture
  }
  return sliceCache[key]
}

// The wall a shop door is cut into, in three pieces: a jamb either side and
// the lintel over the opening. The opening itself is left clear, so the door
// swings in it and the room shows through once it is open. The numbers match
// the doorway in DoorFrame above: 0.6 wide and 0.8 tall, from the floor up.
const FRONT_PIECES = [
  { size: [0.2, 1], at: [-0.4, 0], slice: [0, 0, 0.2, 1] },
  { size: [0.2, 1], at: [0.4, 0], slice: [0.8, 0, 0.2, 1] },
  { size: [0.6, 0.2], at: [0, 0.4], slice: [0.2, 0.8, 0.6, 0.2] },
]

// The front of a shop, painted on the face of the building that looks onto
// the street. `towards` is the way the street lies from the square; a front on
// a doorway is cut around the opening, one on plain wall covers the face.
function ShopFront(props) {
  const { position, trade, towards, doorway } = props
  const url = SHOP_FRONTS[trade]
  const [tx, tz] = towards
  // PANEL_TURN is keyed the other way about, from the open square to the wall
  const turn = PANEL_TURN[`${-tx},${-tz}`] ?? 0
  const spot = [position[0] + tx * 0.501, position[1], position[2] + tz * 0.501]

  if (!doorway) {
    return (
      <mesh position={spot} rotation={[0, turn, 0]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={panelTexture(url)} toneMapped={false} />
      </mesh>
    )
  }

  return (
    <group position={spot} rotation={[0, turn, 0]}>
      {FRONT_PIECES.map((piece) => (
        <mesh key={piece.slice.join(',')} position={[piece.at[0], piece.at[1], 0]}>
          <planeGeometry args={piece.size} />
          <meshBasicMaterial map={panelSlice(url, piece.slice)} toneMapped={false} />
        </mesh>
      ))}
    </group>
  )
}

function BrokenDoor(props) {
  const { position, orientation } = props
  const planks = painted('door_broken')
  const frame = painted(wallsOf(props.orientationTheme || props.theme)[0])
  const isHorizontal = orientation === 'horizontal'

  return (
    <group position={position}>
      {/* The frame is left standing */}
      <mesh position={[0, 0.4, 0]}>
        <boxGeometry args={[isHorizontal ? 0.2 : 1, 0.2, isHorizontal ? 1 : 0.2]} />
        <meshStandardMaterial map={frame.map} normalMap={frame.normalMap} roughness={0.9} />
      </mesh>
      {[-0.4, 0.4].map((offset) => (
        <mesh key={offset} position={[isHorizontal ? 0 : offset, 0, isHorizontal ? offset : 0]}>
          <boxGeometry args={[isHorizontal ? 0.2 : 0.2, 0.8, isHorizontal ? 0.2 : 0.2]} />
          <meshStandardMaterial map={frame.map} normalMap={frame.normalMap} roughness={0.9} />
        </mesh>
      ))}
      {/* What is left of the door. The smashed middle is cut out of the
          texture, so the party can see through the hole. */}
      <mesh rotation={[0, isHorizontal ? Math.PI / 2 : 0, 0]}>
        <planeGeometry args={[0.8, 0.9]} />
        <meshStandardMaterial
          map={planks.map}
          normalMap={planks.normalMap}
          roughness={0.9}
          side={2}
          transparent
          alphaTest={0.5}
        />
      </mesh>
    </group>
  )
}

function DoorFrame(props) {
  const { position, tileType, gridX, gridY } = props;
  
  // The frame matches the dungeon walls; the door itself is painted oak
  const frame = painted(wallsOf(props.theme)[0])
  const door = painted('door_wood')
  const stoneTexture = frame.map
  const stoneNormalMap = frame.normalMap
  const doorTexture = door.map
  const doorNormalMap = door.normalMap
  
  // Determine door properties based on tile type
  // 3 = horizontal_closed, 4 = horizontal_open
  // 5 = vertical_closed, 6 = vertical_open
  const isHorizontal = tileType === 3 || tileType === 4;
  const isOpen = tileType === 4 || tileType === 6;
  
  // Door frame dimensions
  const frameDepth = 0.2;
  const frameWidth = 0.15;
  const doorWidth = 0.6;
  const doorHeight = 0.8;
  
  return (
    <group position={position}>
      {/* Door Frame - Top */}
      <mesh position={[0, 0.4, 0]}>
        <boxGeometry args={[isHorizontal ? frameDepth : 1, 0.2, isHorizontal ? 1 : frameDepth]} />
        <meshStandardMaterial 
          map={stoneTexture}
          normalMap={stoneNormalMap}
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>
      
      {/* Door Frame - Left/Bottom Side */}
      <mesh position={[isHorizontal ? 0 : -0.4, 0, isHorizontal ? -0.4 : 0]}>
        <boxGeometry args={[isHorizontal ? frameDepth : 0.2, 0.8, isHorizontal ? 0.2 : frameDepth]} />
        <meshStandardMaterial 
          map={stoneTexture}
          normalMap={stoneNormalMap}
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>
      
      {/* Door Frame - Right/Top Side */}
      <mesh position={[isHorizontal ? 0 : 0.4, 0, isHorizontal ? 0.4 : 0]}>
        <boxGeometry args={[isHorizontal ? frameDepth : 0.2, 0.8, isHorizontal ? 0.2 : frameDepth]} />
        <meshStandardMaterial 
          map={stoneTexture}
          normalMap={stoneNormalMap}
          roughness={0.9}
          metalness={0.1}
        />
      </mesh>
      
      {/* Door Panel */}
      {!isOpen ? (
        // Closed door - centered in frame
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[isHorizontal ? 0.08 : doorWidth, doorHeight, isHorizontal ? doorWidth : 0.08]} />
          <meshStandardMaterial 
            map={doorTexture}
            normalMap={doorNormalMap}
            roughness={0.8}
            metalness={0.2}
            side={2}
          />
        </mesh>
      ) : (
        // Open door - rotated and offset
        <mesh 
          position={[
            isHorizontal ? 0 : -0.25, 
            0, 
            isHorizontal ? -0.25 : 0
          ]}
          rotation={[0, isHorizontal ? 0 : Math.PI / 2, 0]}
        >
          <boxGeometry args={[doorWidth, doorHeight, 0.08]} />
          <meshStandardMaterial 
            map={doorTexture}
            normalMap={doorNormalMap}
            roughness={0.8}
            metalness={0.2}
            side={2}
          />
        </mesh>
      )}
      
      {/* Floor threshold */}
      <mesh position={[0, -0.49, 0]}>
        <boxGeometry args={[isHorizontal ? 0.15 : 0.8, 0.02, isHorizontal ? 0.8 : 0.15]} />
        <meshStandardMaterial 
          color="#3a3a3a"
          roughness={0.7}
          metalness={0.3}
        />
      </mesh>
    </group>
  )
}

// Look of each humanoid monster model. Scale is applied from the floor up.
const HUMANOID_MODELS = {
  orc: { scale: 1, skin: '#4f7a2e', cloth: '#4a3320', eyes: '#ff2200', tusks: true, ears: false, weapon: 'club', build: 1 },
  goblin: { scale: 0.72, skin: '#8a9a30', cloth: '#5a2a1a', eyes: '#ffcc00', tusks: false, ears: true, weapon: 'dagger', build: 0.85 },
  skeleton: { scale: 0.95, skin: '#d8d0b8', cloth: '#2e2e2e', eyes: '#66ccff', tusks: false, ears: false, weapon: 'sword', build: 0.7 },
}

// Materials for a monster: `flesh` and `cloth` flash red when it is hit
function useMonsterMaterials(skinColor, clothColor, hitCount) {
  const flash = useRef(0)
  const lastHitCount = useRef(hitCount)

  const flesh = useMemo(() => new THREE.MeshStandardMaterial({ color: skinColor, roughness: 0.8 }), [skinColor])
  const cloth = useMemo(() => new THREE.MeshStandardMaterial({ color: clothColor, roughness: 0.9 }), [clothColor])
  const wood = useMemo(() => new THREE.MeshStandardMaterial({ color: '#6b4a2b', roughness: 0.9 }), [])
  const metal = useMemo(() => new THREE.MeshStandardMaterial({ color: '#9aa0a8', roughness: 0.3, metalness: 0.8 }), [])
  const bone = useMemo(() => new THREE.MeshStandardMaterial({ color: '#e8e0c8', roughness: 0.5 }), [])

  useEffect(() => () => [flesh, cloth, wood, metal, bone].forEach(m => m.dispose()), [flesh, cloth, wood, metal, bone])

  useEffect(() => {
    if (hitCount !== lastHitCount.current) {
      flash.current = 1
      lastHitCount.current = hitCount
    }
  }, [hitCount])

  useFrame((state, delta) => {
    flash.current = Math.max(0, flash.current - delta * 3)
    flesh.emissive.setRGB(flash.current * 0.9, 0, 0)
    cloth.emissive.setRGB(flash.current * 0.6, 0, 0)
  })

  return { flesh, cloth, wood, metal, bone }
}

function Eyes({ color, y, z, spread }) {
  return [-spread, spread].map((x) => (
    <mesh key={x} position={[x, y, z]}>
      <sphereGeometry args={[0.015, 8, 8]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.5} />
    </mesh>
  ))
}

function HumanoidMonster({ monster }) {
  const look = HUMANOID_MODELS[monster.model] || HUMANOID_MODELS.orc
  const { flesh, cloth, wood, metal, bone } = useMonsterMaterials(look.skin, look.cloth, monster.hitCount)
  const bodyRef = useRef()
  const w = look.build

  useFrame((state) => {
    if (bodyRef.current) {
      bodyRef.current.position.y = 0.5 + Math.sin(state.clock.elapsedTime * 2.5 + monster.x * 3 + monster.y) * 0.015
    }
  })

  return (
    <group position={[0, -0.5, 0]} scale={look.scale}>
      <group ref={bodyRef} position={[0, 0.5, 0]}>
        {/* Legs */}
        <mesh position={[-0.08, -0.36, 0]} material={monster.model === 'skeleton' ? flesh : cloth}><boxGeometry args={[0.11 * w, 0.28, 0.12 * w]} /></mesh>
        <mesh position={[0.08, -0.36, 0]} material={monster.model === 'skeleton' ? flesh : cloth}><boxGeometry args={[0.11 * w, 0.28, 0.12 * w]} /></mesh>
        {/* Torso and belt */}
        <mesh position={[0, -0.06, 0]} material={flesh}><boxGeometry args={[0.34 * w, 0.32, 0.2 * w]} /></mesh>
        <mesh position={[0, -0.2, 0]} material={cloth}><boxGeometry args={[0.36 * w, 0.06, 0.22 * w]} /></mesh>
        {/* Head and jaw */}
        <mesh position={[0, 0.2, 0.01]} material={flesh}><sphereGeometry args={[0.11, 16, 12]} /></mesh>
        <mesh position={[0, 0.14, 0.05]} material={flesh}><boxGeometry args={[0.14, 0.06, 0.1]} /></mesh>
        {look.tusks && [-0.045, 0.045].map((x) => (
          <mesh key={x} position={[x, 0.19, 0.1]} material={bone}><coneGeometry args={[0.012, 0.05, 6]} /></mesh>
        ))}
        {look.ears && [-1, 1].map((side) => (
          <mesh key={side} position={[side * 0.13, 0.22, 0]} rotation={[0, 0, -side * Math.PI / 2.5]} material={flesh}><coneGeometry args={[0.03, 0.12, 6]} /></mesh>
        ))}
        <Eyes color={look.eyes} y={0.23} z={0.095} spread={0.04} />
        {/* Arms */}
        <mesh position={[-0.23 * w, -0.05, 0]} rotation={[0, 0, 0.15]} material={flesh}><boxGeometry args={[0.09 * w, 0.3, 0.09 * w]} /></mesh>
        <mesh position={[0.23 * w, -0.02, 0.06]} rotation={[-0.6, 0, -0.15]} material={flesh}><boxGeometry args={[0.09 * w, 0.3, 0.09 * w]} /></mesh>
        {/* Weapon raised in the right hand */}
        {look.weapon === 'club' && (
          <mesh position={[0.26, 0.12, 0.2]} rotation={[0.9, 0, 0]} material={wood}><cylinderGeometry args={[0.035, 0.02, 0.38, 8]} /></mesh>
        )}
        {look.weapon === 'dagger' && (
          <mesh position={[0.22, 0.02, 0.2]} rotation={[0.9, 0, 0]} material={metal}><boxGeometry args={[0.02, 0.18, 0.04]} /></mesh>
        )}
        {look.weapon === 'sword' && (
          <mesh position={[0.2, 0.14, 0.22]} rotation={[0.9, 0, 0]} material={metal}><boxGeometry args={[0.025, 0.4, 0.05]} /></mesh>
        )}
      </group>
    </group>
  )
}

function RatMonster({ monster }) {
  const { flesh, cloth } = useMonsterMaterials('#6b5a4a', '#e0a0a0', monster.hitCount)
  const bodyRef = useRef()

  // Rats twitch faster and closer to the ground than humanoids
  useFrame((state) => {
    if (bodyRef.current) {
      bodyRef.current.position.y = Math.abs(Math.sin(state.clock.elapsedTime * 6 + monster.x * 3 + monster.y)) * 0.02
    }
  })

  return (
    <group ref={bodyRef}>
      {/* Body and head */}
      <mesh position={[0, -0.38, 0]} scale={[0.14, 0.1, 0.24]} material={flesh}><sphereGeometry args={[1, 16, 12]} /></mesh>
      <mesh position={[0, -0.35, 0.22]} scale={[0.07, 0.065, 0.1]} material={flesh}><sphereGeometry args={[1, 12, 10]} /></mesh>
      <mesh position={[0, -0.36, 0.32]} material={cloth}><sphereGeometry args={[0.015, 8, 8]} /></mesh>
      {[-1, 1].map((side) => (
        <mesh key={side} position={[side * 0.05, -0.28, 0.2]} material={cloth}><sphereGeometry args={[0.03, 8, 8]} /></mesh>
      ))}
      <Eyes color="#ff2200" y={-0.32} z={0.28} spread={0.035} />
      {/* Legs */}
      {[[-0.09, 0.12], [0.09, 0.12], [-0.09, -0.12], [0.09, -0.12]].map(([x, z]) => (
        <mesh key={x + ':' + z} position={[x, -0.47, z]} material={flesh}><boxGeometry args={[0.04, 0.06, 0.05]} /></mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, -0.42, -0.4]} rotation={[Math.PI / 2 + 0.2, 0, 0]} material={cloth}><cylinderGeometry args={[0.008, 0.02, 0.36, 6]} /></mesh>
    </group>
  )
}

// The trainer's desk: a writing desk with a robed figure behind it, and a
// lantern so the corner reads as somewhere to stop.
function TrainerDesk() {
  const flame = useRef()

  useFrame((state) => {
    if (flame.current) {
      const t = state.clock.elapsedTime
      flame.current.scale.setScalar(0.9 + Math.sin(t * 9) * 0.08 + Math.sin(t * 3.3) * 0.05)
    }
  })

  return (
    <group position={[0, -0.5, 0]}>
      {/* Desk top and legs */}
      <mesh position={[0, 0.34, -0.1]}><boxGeometry args={[0.8, 0.06, 0.4]} /><meshStandardMaterial color="#5a3a1e" roughness={0.85} /></mesh>
      {[[-0.34, -0.24], [0.34, -0.24], [-0.34, 0.04], [0.34, 0.04]].map(([x, z]) => (
        <mesh key={`${x}:${z}`} position={[x, 0.16, z - 0.06]}><boxGeometry args={[0.06, 0.34, 0.06]} /><meshStandardMaterial color="#4a2f18" roughness={0.9} /></mesh>
      ))}
      {/* Ledger on the desk */}
      <mesh position={[-0.12, 0.38, -0.1]} rotation={[0, 0.2, 0]}><boxGeometry args={[0.26, 0.03, 0.2]} /><meshStandardMaterial color="#d8c9a0" roughness={0.9} /></mesh>

      {/* Lantern, with a flickering flame */}
      <mesh position={[0.28, 0.42, -0.14]}><boxGeometry args={[0.1, 0.12, 0.1]} /><meshStandardMaterial color="#6a5a3a" metalness={0.6} roughness={0.5} /></mesh>
      <mesh ref={flame} position={[0.28, 0.42, -0.14]}>
        <sphereGeometry args={[0.04, 10, 8]} />
        <meshBasicMaterial color="#ffcc55" toneMapped={false} />
      </mesh>

      {/* The trainer: robe, head, hood */}
      <mesh position={[0, 0.3, -0.34]}><cylinderGeometry args={[0.13, 0.22, 0.6, 10]} /><meshStandardMaterial color="#3b2f5a" roughness={0.9} /></mesh>
      <mesh position={[0, 0.66, -0.34]}><sphereGeometry args={[0.1, 14, 12]} /><meshStandardMaterial color="#d8b08a" roughness={0.8} /></mesh>
      <mesh position={[0, 0.7, -0.36]}><sphereGeometry args={[0.12, 14, 12]} /><meshStandardMaterial color="#2e2448" roughness={0.95} /></mesh>
      {/* Hands resting on the desk */}
      {[-0.16, 0.16].map((x) => (
        <mesh key={x} position={[x, 0.38, -0.2]}><sphereGeometry args={[0.05, 10, 8]} /><meshStandardMaterial color="#d8b08a" roughness={0.8} /></mesh>
      ))}
    </group>
  )
}

// ---------------------------------------------------------------- counters
//
// What a trade actually is, as far as the party is concerned: something to
// walk up to in a room. The building front outside is only the sign.

// A candle flame that wavers, used by a couple of the counters below
function Flame(props) {
  const flame = useRef()
  useFrame((state) => {
    if (flame.current) {
      const t = state.clock.elapsedTime + (props.offset || 0)
      flame.current.scale.setScalar(0.9 + Math.sin(t * 9) * 0.1 + Math.sin(t * 3.3) * 0.06)
    }
  })
  return (
    <mesh ref={flame} position={props.position}>
      <sphereGeometry args={[props.size || 0.03, 8, 6]} />
      <meshBasicMaterial color={props.color || '#ffcc55'} toneMapped={false} />
    </mesh>
  )
}

// The temple: a stone altar under a gilded sunburst, candles either side
function TempleAltar() {
  return (
    <group position={[0, -0.5, 0]}>
      <mesh position={[0, 0.16, -0.2]}><boxGeometry args={[0.8, 0.32, 0.36]} /><meshStandardMaterial color="#b9b2a2" roughness={0.9} /></mesh>
      <mesh position={[0, 0.35, -0.2]}><boxGeometry args={[0.92, 0.07, 0.46]} /><meshStandardMaterial color="#cfc8b6" roughness={0.8} /></mesh>
      {/* An altar cloth hanging over the front */}
      <mesh position={[0, 0.2, -0.02]}><boxGeometry args={[0.6, 0.26, 0.02]} /><meshStandardMaterial color="#7a2630" roughness={0.95} /></mesh>
      {/* The sunburst above it */}
      <mesh position={[0, 0.78, -0.34]}><cylinderGeometry args={[0.13, 0.13, 0.03, 16]} /><meshStandardMaterial color="#d9a93c" metalness={0.7} roughness={0.35} /></mesh>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <mesh key={i} position={[Math.sin(i * 0.785) * 0.2, 0.78 + Math.cos(i * 0.785) * 0.2, -0.34]} rotation={[0, 0, -i * 0.785]}>
          <boxGeometry args={[0.03, 0.12, 0.02]} /><meshStandardMaterial color="#d9a93c" metalness={0.7} roughness={0.35} />
        </mesh>
      ))}
      {/* Candles */}
      {[-0.33, 0.33].map((x) => (
        <group key={x}>
          <mesh position={[x, 0.46, -0.24]}><cylinderGeometry args={[0.03, 0.035, 0.16, 8]} /><meshStandardMaterial color="#e8e0cc" roughness={0.9} /></mesh>
          <Flame position={[x, 0.57, -0.24]} offset={x} />
        </group>
      ))}
      <pointLight position={[0, 0.6, -0.1]} intensity={0.45} distance={2.4} color="#ffd9a0" />
    </group>
  )
}

// The magic school: a lectern with a book, and a slowly turning glyph above it
function MagicLectern() {
  const orb = useRef()
  useFrame((state) => {
    if (orb.current) {
      const t = state.clock.elapsedTime
      orb.current.position.y = 0.72 + Math.sin(t * 1.6) * 0.03
      orb.current.rotation.y = t * 0.6
    }
  })

  return (
    <group position={[0, -0.5, 0]}>
      <mesh position={[0, 0.2, -0.22]}><cylinderGeometry args={[0.07, 0.14, 0.4, 8]} /><meshStandardMaterial color="#3a2d4e" roughness={0.85} /></mesh>
      <mesh position={[0, 0.44, -0.2]} rotation={[-0.5, 0, 0]}><boxGeometry args={[0.5, 0.04, 0.34]} /><meshStandardMaterial color="#4a3a22" roughness={0.8} /></mesh>
      {/* An open book on the slope */}
      <mesh position={[-0.11, 0.49, -0.17]} rotation={[-0.5, 0, 0.04]}><boxGeometry args={[0.22, 0.03, 0.26]} /><meshStandardMaterial color="#e2d7bb" roughness={0.9} /></mesh>
      <mesh position={[0.11, 0.49, -0.17]} rotation={[-0.5, 0, -0.04]}><boxGeometry args={[0.22, 0.03, 0.26]} /><meshStandardMaterial color="#e2d7bb" roughness={0.9} /></mesh>
      {/* The glyph turning over it */}
      <group ref={orb} position={[0, 0.72, -0.18]}>
        <mesh><torusGeometry args={[0.09, 0.012, 8, 20]} /><meshBasicMaterial color="#b07cff" toneMapped={false} /></mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.06, 0.01, 8, 16]} /><meshBasicMaterial color="#d8c0ff" toneMapped={false} /></mesh>
      </group>
      <pointLight position={[0, 0.72, -0.1]} intensity={0.5} distance={2.2} color="#9a6cff" />
    </group>
  )
}

// The market: a counter with goods on it and stock stacked behind
function MarketCounter() {
  return (
    <group position={[0, -0.5, 0]}>
      <mesh position={[0, 0.3, -0.12]}><boxGeometry args={[0.9, 0.07, 0.34]} /><meshStandardMaterial color="#6b4524" roughness={0.85} /></mesh>
      <mesh position={[0, 0.14, -0.12]}><boxGeometry args={[0.84, 0.26, 0.26]} /><meshStandardMaterial color="#4e3119" roughness={0.9} /></mesh>
      {/* Crates and a barrel behind the counter */}
      <mesh position={[-0.3, 0.16, -0.4]}><boxGeometry args={[0.26, 0.26, 0.26]} /><meshStandardMaterial color="#5c3d20" roughness={0.9} /></mesh>
      <mesh position={[-0.28, 0.42, -0.42]}><boxGeometry args={[0.2, 0.2, 0.2]} /><meshStandardMaterial color="#6b4524" roughness={0.9} /></mesh>
      <mesh position={[0.3, 0.2, -0.4]}><cylinderGeometry args={[0.15, 0.13, 0.4, 12]} /><meshStandardMaterial color="#5a3a1e" roughness={0.9} /></mesh>
      {/* Goods laid out: a helm, a potion, coins */}
      <mesh position={[-0.22, 0.4, -0.12]}><sphereGeometry args={[0.07, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color="#9aa0a6" metalness={0.75} roughness={0.35} /></mesh>
      <mesh position={[0.04, 0.38, -0.1]}><cylinderGeometry args={[0.03, 0.04, 0.1, 8]} /><meshStandardMaterial color="#8e2f3a" roughness={0.4} /></mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[0.26 + i * 0.015, 0.35 + i * 0.012, -0.08]} rotation={[Math.PI / 2, 0, i]}>
          <cylinderGeometry args={[0.03, 0.03, 0.008, 10]} /><meshStandardMaterial color="#d9a93c" metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
    </group>
  )
}

// The tavern: a bar with barrels behind it and a lantern over it
function TavernBar() {
  return (
    <group position={[0, -0.5, 0]}>
      <mesh position={[0, 0.32, -0.1]}><boxGeometry args={[0.95, 0.08, 0.36]} /><meshStandardMaterial color="#5c3b1e" roughness={0.7} /></mesh>
      <mesh position={[0, 0.15, -0.1]}><boxGeometry args={[0.88, 0.3, 0.28]} /><meshStandardMaterial color="#432a14" roughness={0.9} /></mesh>
      {/* Barrels racked behind the bar */}
      {[-0.26, 0.26].map((x) => (
        <mesh key={x} position={[x, 0.26, -0.42]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.17, 0.17, 0.34, 12]} /><meshStandardMaterial color="#5a3a1e" roughness={0.9} />
        </mesh>
      ))}
      {/* Tankards on the bar */}
      {[-0.3, -0.12, 0.22].map((x, i) => (
        <mesh key={x} position={[x, 0.41, -0.08]}>
          <cylinderGeometry args={[0.045, 0.04, 0.1, 10]} /><meshStandardMaterial color={i === 1 ? '#b9b2a2' : '#8a7a5a'} metalness={0.4} roughness={0.6} />
        </mesh>
      ))}
      {/* A lantern hanging over the bar */}
      <mesh position={[0.1, 0.72, -0.2]}><boxGeometry args={[0.11, 0.13, 0.11]} /><meshStandardMaterial color="#6a5a3a" metalness={0.6} roughness={0.5} /></mesh>
      <Flame position={[0.1, 0.72, -0.2]} size={0.045} offset={1.7} />
      <pointLight position={[0.1, 0.66, -0.1]} intensity={0.55} distance={2.6} color="#ffbb66" />
    </group>
  )
}

// Which counter belongs to which square
const COUNTERS = {
  9: TrainerDesk,
  12: MagicLectern,
  13: MarketCounter,
  14: TempleAltar,
  15: TavernBar,
}

// A monster on the map. It turns to face the party, idles and flashes red when hit.
// `facing` turns the model's front (+z); `scale` shrinks it from the floor up.
function Monster(props) {
  const { monster, position, facing, scale = 1 } = props;

  return (
    <group position={[position[0], -0.5, position[2]]} rotation={[0, facing, 0]} scale={scale}>
      <group position={[0, 0.5, 0]}>
        {monster.model === 'rat' ? <RatMonster monster={monster} /> : <HumanoidMonster monster={monster} />}
      </group>
    </group>
  )
}

// Out of doors the sky is a single plane high above the town. Drawn as a
// ceiling tile per square, the same clouds repeat every yard and the grid of
// seams gives the game away.
function Sky(props) {
  const { centre, size } = props
  const texture = useMemo(() => {
    const { map } = painted('ceiling_sky')
    map.wrapS = THREE.RepeatWrapping
    map.wrapT = THREE.RepeatWrapping
    map.repeat.set(size / 10, size / 10)
    return map
  }, [size])

  return (
    <mesh position={[centre[0], 0.5, centre[1]]} rotation={[Math.PI / 2, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  )
}

function Box(props) {
  // This reference gives us direct access to the THREE.Mesh object
  const ref = useRef()
  
  // Painted textures for walls, floor and ceiling, in this level's style
  const surface = useMemo(() => {
    if (props.isWall) return wallMaps(props.theme, props.variant || 0)
    if (props.isFloor) return floorMaps(props.theme, props.floor, props.carpeted)
    if (props.isCeiling) return ceilingMaps(props.theme)
    return null
  }, [props.isWall, props.isFloor, props.isCeiling, props.variant, props.theme, props.floor, props.carpeted])
  
  // Hold state for hovered and clicked events
  // Subscribe this component to the render-loop, rotate the mesh every frame
  //useFrame((state, delta) => (ref.current.rotation.x += delta))
  // Return the view, these are regular Threejs elements expressed in JSX
  return (
    <mesh
      {...props}
      ref={ref}>
      <boxGeometry args={[1,1,1]} />
      {surface ? (
        <meshStandardMaterial 
          map={surface.map}
          normalMap={surface.normalMap}
          roughness={props.isFloor ? 0.95 : 0.9}
          metalness={props.isFloor ? 0.05 : 0.1}
        />
      ) : (
        <meshStandardMaterial color={props.color} />
      )}
    </mesh>
  )
}

const RESIZE_EVENTS = [
  [window, 'resize'],
  [window, 'orientationchange'],
  [document, 'fullscreenchange'],
  [window.visualViewport, 'resize'],
];

class ViewPort extends Component {

  ratio = window.devicePixelRatio;

  state = { dpr: Math.min(2, window.devicePixelRatio) };

  componentDidMount(){
    this.props.context.update({
      eventLog:[...this.props.context.eventLog, {type:'dev', message: 'Heading: ' + this.props.context.player_heading}, {type:'dev', message: 'Ratio: ' + this.ratio}]
    })
    RESIZE_EVENTS.forEach(([target, event]) => target && target.addEventListener(event, this.scheduleDprUpdate));
    this.scheduleDprUpdate();
  }

  componentWillUnmount(){
    RESIZE_EVENTS.forEach(([target, event]) => target && target.removeEventListener(event, this.scheduleDprUpdate));
    cancelAnimationFrame(this.dprFrame);
  }

  // The canvas is laid out at 1530x810 but #mainFrame is scaled to fit the
  // screen, so render only as many pixels as are actually shown: device pixel
  // ratio times the frame's scale. Waits a frame so App's resize handler has
  // applied the new scale first.
  scheduleDprUpdate = () => {
    cancelAnimationFrame(this.dprFrame);
    this.dprFrame = requestAnimationFrame(() => {
      const frame = document.getElementById('mainFrame');
      const frameScale = frame ? frame.getBoundingClientRect().width / 1920 : 1;
      const dpr = Math.min(2, Math.max(0.5, window.devicePixelRatio * frameScale));
      if (Math.abs(dpr - this.state.dpr) > 0.01) {
        this.setState({ dpr });
      }
    });
  }
  
  // Fighting monsters stand in a lineup on the party's square (so they stay in
  // view as the party turns); the rest stand on their own squares facing the party
  renderMonsters() {
    const { monsters, combat, player_coords, player_heading } = this.props.context;
    const layout = combatFoeLayout(monsters, combat, player_coords, player_heading);
    return monsters.map((monster) => {
      const spot = layout[monster.id] || {
        position: [monster.x, 0, monster.y],
        facing: Math.atan2(player_coords[0] - monster.x, player_coords[1] - monster.y),
      };
      return <Monster key={monster.id} monster={monster} {...spot} />;
    });
  }

  checkWallOrientation = (x, y) => {
    const map = this.props.context.player_map;
    // Check if door has walls on left/right (vertical orientation)
    if (x > 0 && x < map[0].length - 1) {
      if (map[y][x - 1] !== 0 && map[y][x + 1] !== 0) {
        return true; // Vertical wall
      }
    }
    return false; // Horizontal wall
  }

  // Which trade's front this square's building carries, if any
  frontAt(x, y) {
    const { surrounds } = this.props.context;
    const front = surrounds && surrounds[y] && surrounds[y][x];
    return front && SHOP_FRONTS[front] ? front : null;
  }

  // The ways out of a square onto open ground. A front on plain wall is
  // painted on every such face; a front on a doorway goes on the street side
  // only, so the shop is not advertised to its own customers from indoors.
  streetsBeside(x, y) {
    const map = this.props.context.player_map;
    const tileAt = (cx, cy) => (map[cy] ? map[cy][cx] : undefined);
    const open = (cx, cy) => {
      const tile = tileAt(cx, cy);
      return tile !== undefined && tile !== 1 && tile !== 2;
    };
    const ways = [[0, -1], [1, 0], [0, 1], [-1, 0]].filter(([dx, dy]) => open(x + dx, y + dy));

    const tile = tileAt(x, y);
    if (tile < 3 || tile > 6 || ways.length < 2) return ways;

    // The street is the side away from the counter, or failing that the side
    // with more open ground around it
    const elbowRoom = ([dx, dy]) =>
      (COUNTERS[tileAt(x + dx, y + dy)] ? -10 : 0) +
      [[0, -1], [1, 0], [0, 1], [-1, 0]].filter(([ox, oy]) => open(x + dx + ox, y + dy + oy)).length;
    return [ways.slice().sort((a, b) => elbowRoom(b) - elbowRoom(a))[0]];
  }

  // What a square is floored with, if the map was painted rather than generated
  floorAt(x, y) {
    const { floors } = this.props.context;
    return (floors && floors[y] && floors[y][x]) || null;
  }

  // Which of a square's neighbours are carpeted, so the right piece is laid
  carpetedAround(x, y) {
    const carpet = (cx, cy) => this.floorAt(cx, cy) === CARPET;
    return { north: carpet(x, y - 1), east: carpet(x + 1, y), south: carpet(x, y + 1), west: carpet(x - 1, y) };
  }

  render() {
    const theme = this.props.context.theme;
    // The town is out of doors: flat daylight, and no torch at the party's back
    const outdoors = !!(theme && theme.outdoor);
    // A painted town can have its own way down: a sewer, a tower, a castle gate
    const { entrance, entrancePanel } = this.props.context;
    return (
      <div id="viewPort" ref={(mount) => {this.mount = mount }}>
        {/* offsetSize: measure the unscaled layout size. The default bounding-rect
            measurement includes #mainFrame's CSS scale, which would shrink the
            canvas a second time whenever the frame is scaled down (e.g. on phones). */}
        <Canvas dpr={this.state.dpr} resize={{ offsetSize: true }}>
          <ambientLight intensity={outdoors ? 0.78 : 0.48} />
          <pointLight position={[10, 10, 10]} intensity={outdoors ? 0.55 : 0.8} />
          {outdoors &&
            <Sky centre={this.props.context.player_coords} size={DRAW_RADIUS * 2 + 6} />
          }
          {!outdoors &&
            <pointLight position={coordsToPosition(this.props.context.player_coords, this.props.context.player_heading)} intensity={0.5} />
          }

          {this.props.context.player_map.map((row,ri)=>{
                return row.map((column, ci)=>{
                  // Squares far from the party are never seen, so skip them
                  if (Math.abs(ci - this.props.context.player_coords[0]) > DRAW_RADIUS ||
                      Math.abs(ri - this.props.context.player_coords[1]) > DRAW_RADIUS) {
                    return null;
                  }
                  return (
                    <Fragment key={'f'+ci+ri}>
                      <Box
                        key={ci+':'+ri+'floor'}
                        position={[0 + ci,-1,-0 + ri]}
                        isFloor={true}
                        theme={theme}
                        floor={this.floorAt(ci, ri)}
                        carpeted={this.floorAt(ci, ri) === CARPET ? this.carpetedAround(ci, ri) : null}
                      />
                      {!outdoors &&
                        <Box key={ci+':'+ri+'ceiling'}position={[0 + ci,1,-0 + ri]} isCeiling={true} theme={theme}/>
                      }
                      {(column === 1 || column === 2) && 
                        <Box key={ci+':'+ri}position={[0 + ci,0,-0 + ri]} isWall={true} variant={wallVariant(ci, ri)} theme={theme}/>
                      }
                      {(column >= 3 && column <= 6) && 
                        <DoorFrame 
                          key={ci+':'+ri}
                          position={[0 + ci,0,-0 + ri]} 
                          tileType={column}
                          theme={theme}
                          gridX={ci}
                          gridY={ri}
                        />
                      }
                      {(column === 11) &&
                        <BrokenDoor
                          key={ci+':'+ri}
                          theme={theme}
                          position={[0 + ci,0,-0 + ri]}
                          orientation={(this.props.context.player_map[ri][ci - 1] === 1 ||
                                        this.props.context.player_map[ri][ci - 1] === 2) ? 'vertical' : 'horizontal'}
                        />
                      }
                      {WALL_PANELS[column] &&
                        <WallFront
                          key={ci+':'+ri}
                          position={[0 + ci,0,-0 + ri]}
                          tile={column}
                          panel={entrance && entrance[0] === ci && entrance[1] === ri ? entrancePanel : null}
                          wall={wallBeside(this.props.context.player_map, [ci, ri]) || [0, -1]}
                        />
                      }
                      {(column === 8) && 
                        <Chest 
                          key={ci+':'+ri}
                          position={[0 + ci,0,-0 + ri]} 
                        />
                      }
                      {COUNTERS[column] &&
                        <group key={ci+':'+ri} position={[0 + ci,0,-0 + ri]}>
                          {React.createElement(COUNTERS[column])}
                        </group>
                      }
                      {this.frontAt(ci, ri) && this.streetsBeside(ci, ri).map(([dx, dz]) => (
                        <ShopFront
                          key={`${ci}:${ri}:${dx},${dz}`}
                          position={[ci, 0, ri]}
                          trade={this.frontAt(ci, ri)}
                          towards={[dx, dz]}
                          doorway={column >= 3 && column <= 6}
                        />
                      ))}

                    </Fragment>
                  )
                 })
            })}
          {this.renderMonsters()}
          {this.props.context.effects.map((effect) => (
            <Effect key={effect.id} effect={effect} />
          ))}
          <SpellLight effects={this.props.context.effects} />
          <PerspectiveCamera position={coordsToPosition(this.props.context.player_coords, this.props.context.player_heading)} rotation={[0,deg2rad(this.props.context.player_heading),0]} fov={58} makeDefault={true}/>
        </Canvas>
      </div>
    );
  }
}

export default withContext(ViewPort);